/**
 * EnvoiSignatureModal — Envoi de la Proposition ESS BATICO par email
 * pour signature électronique (V36.12 — 28/05/2026).
 *
 * Le PDF est attaché et un CTA "Signer la proposition" mène à la page
 * de signature interne (lien token-signé).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { getAuthHeader } from '../../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EnvoiSignatureModal({
  open, onOpenChange, devisId, defaultEmail = '', defaultName = '',
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail || '');
      setName(defaultName || '');
      setMessage('');
    }
  }, [open, defaultEmail, defaultName]);

  const handleSend = async () => {
    const trimmed = (email || '').trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error('Adresse email invalide');
      return;
    }
    if (!devisId) {
      toast.error('Référence proposition manquante');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/batico/proposition/${encodeURIComponent(devisId)}/send-for-signature`,
        {
          to_email: trimmed,
          to_name: (name || '').trim() || null,
          custom_message: (message || '').trim() || null,
        },
        { headers: getAuthHeader() },
      );
      if (res.data?.sent) {
        toast.success(
          res.data.pdf_attached
            ? 'Email envoyé avec le PDF en pièce jointe'
            : 'Email envoyé (PDF non joint — sera ajouté à la régénération suivante)',
        );
        onOpenChange?.(false);
      } else {
        toast.error('Envoi échoué : ' + (res.data?.reason || 'erreur inconnue'));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="envoi-signature-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-900">
            <Mail className="h-5 w-5" />
            Envoyer pour signature
          </DialogTitle>
          <DialogDescription>
            Le destinataire recevra le PDF de la proposition en pièce jointe
            ainsi qu'un lien sécurisé pour signer électroniquement.
            {devisId && (
              <span className="block mt-1 font-mono text-[11px] text-slate-500">
                Référence : {devisId}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="signature-to-email" className="text-sm font-medium">
              Adresse email du destinataire <span className="text-red-500">*</span>
            </Label>
            <Input
              id="signature-to-email"
              type="email"
              placeholder="signataire@exemple.fr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sending}
              className="mt-1"
              data-testid="signature-to-email-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Vous pouvez modifier l'adresse pré-remplie (membre par défaut).
            </p>
          </div>

          <div>
            <Label htmlFor="signature-to-name" className="text-sm font-medium">
              Nom du destinataire (optionnel)
            </Label>
            <Input
              id="signature-to-name"
              placeholder="Nom et prénom"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={sending}
              className="mt-1"
              data-testid="signature-to-name-input"
            />
          </div>

          <div>
            <Label htmlFor="signature-message" className="text-sm font-medium">
              Message personnalisé (optionnel)
            </Label>
            <Textarea
              id="signature-message"
              placeholder="Bonjour, merci de bien vouloir signer cette proposition…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              rows={3}
              className="mt-1"
              maxLength={2000}
              data-testid="signature-message-input"
            />
            <p className="text-[11px] text-slate-500 mt-1 text-right">
              {message.length}/2000
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={sending}
            data-testid="signature-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !email}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
            data-testid="signature-send-btn"
          >
            {sending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
